import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { getMe } from "../lib/api/auth";
import { ApiError, registerUnauthorizedHandler } from "../lib/api/client";
import { queryClient } from "../lib/queryClient";
import type { AppConfig, User } from "../lib/api/types";

type AuthStatus = "loading" | "authed" | "anon";

interface AuthContextValue {
  status: AuthStatus;
  user: User | null;
  config: AppConfig | null;
  refresh: () => Promise<void>;
  signOutLocal: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<User | null>(null);
  const [config, setConfig] = useState<AppConfig | null>(null);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    try {
      const me = await getMe();
      setUser(me.user);
      setConfig(me.config);
      setStatus("authed");
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setUser(null);
        setConfig(null);
        setStatus("anon");
        return;
      }
      // Network/5xx on boot: treat as anonymous so the app can render Login.
      setUser(null);
      setConfig(null);
      setStatus("anon");
    }
  }, []);

  const signOutLocal = useCallback(() => {
    setUser(null);
    setConfig(null);
    setStatus("anon");
  }, []);

  // Register the single global 401 handler exactly once.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  useEffect(() => {
    registerUnauthorizedHandler(() => {
      setUser(null);
      setConfig(null);
      setStatus("anon");
      queryClient.clear();
      navigateRef.current("/login", { replace: true });
    });
  }, []);

  // Boot: probe session.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, config, refresh, signOutLocal }),
    [status, user, config, refresh, signOutLocal],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
